import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";
import { useEffect } from "react";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";

// Types
export interface WorkOrder {
  id: string;
  workspace_id: string;
  pipeline_property_id: string;
  created_by_user_id: string;
  assigned_to: string | null;
  title: string;
  description: string | null;
  category: string;
  priority: string;
  status: string;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  cost_estimate: number | null;
  cost_actual: number | null;
  vendor_name: string | null;
  vendor_phone: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface RentPayment {
  id: string;
  workspace_id: string;
  pipeline_property_id: string;
  tenant_name: string;
  amount: number;
  due_date: string;
  paid_date: string | null;
  payment_method: string | null;
  status: string;
  late_fee: number;
  notes: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface TenantMessage {
  id: string;
  workspace_id: string;
  pipeline_property_id: string;
  tenant_name: string;
  tenant_contact: string | null;
  sender_type: string;
  message: string;
  read: boolean;
  created_by_user_id: string | null;
  created_at: string;
}

export interface PMSettings {
  id: string;
  workspace_id: string;
  pipeline_property_id: string | null;
  management_mode: string;
  pm_company_name: string | null;
  pm_contact_name: string | null;
  pm_contact_email: string | null;
  pm_contact_phone: string | null;
  appfolio_property_id: string | null;
  appfolio_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export function usePropertyManagement() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  // ---- Work Orders ----
  const { data: workOrders = [], isLoading: loadingWorkOrders } = useQuery({
    queryKey: ["work-orders", workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select("*")
        .eq("workspace_id", workspaceId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as WorkOrder[];
    },
    enabled: !!workspaceId,
  });

  const createWorkOrder = useMutation({
    mutationFn: async (wo: Partial<WorkOrder>) => {
      const { error } = await supabase.from("work_orders").insert({
        ...wo,
        workspace_id: workspaceId,
        created_by_user_id: user!.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-orders", workspaceId] });
      toast.success("Work order created");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to create work order")),
  });

  const updateWorkOrder = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<WorkOrder> & { id: string }) => {
      const { data: rows, error } = await supabase
        .from("work_orders")
        .update(data as never)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this work order");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-orders", workspaceId] });
      toast.success("Work order updated");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update work order")),
  });

  const deleteWorkOrder = useMutation({
    mutationFn: async (id: string) => {
      const { data: rows, error } = await supabase
        .from("work_orders")
        .delete()
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "delete this work order");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-orders", workspaceId] });
      toast.success("Work order deleted");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to delete work order")),
  });

  // ---- Rent Payments ----
  const { data: rentPayments = [], isLoading: loadingPayments } = useQuery({
    queryKey: ["rent-payments", workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rent_payments")
        .select("*")
        .eq("workspace_id", workspaceId!)
        .order("due_date", { ascending: false });
      if (error) throw error;
      return data as unknown as RentPayment[];
    },
    enabled: !!workspaceId,
  });

  const createPayment = useMutation({
    mutationFn: async (p: Partial<RentPayment>) => {
      const { error } = await supabase.from("rent_payments").insert({
        ...p,
        workspace_id: workspaceId,
        created_by_user_id: user!.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rent-payments", workspaceId],
      });
      toast.success("Payment recorded");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to record payment")),
  });

  const updatePayment = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<RentPayment> & { id: string }) => {
      const { data: rows, error } = await supabase
        .from("rent_payments")
        .update(data as never)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this payment");
    },
    onSuccess: () => {
      // An edited amount, status, or date changes actual rental income, so the
      // same derived caches a delete touches have to be refreshed here too.
      for (const key of [
        ["rent-payments", workspaceId],
        ["rent_payments_owned", workspaceId],
        ["property-rent-payments"],
        ["accounting-rents", workspaceId],
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      toast.success("Payment updated");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update payment")),
  });

  const deletePayment = useMutation({
    mutationFn: async (id: string) => {
      const { data: rows, error } = await supabase
        .from("rent_payments")
        .delete()
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "delete this payment");
    },
    onSuccess: () => {
      // A removed payment changes actual rental income, so every cache derived
      // from `rent_payments` has to drop it too — the Properties cash-flow
      // metrics, the property detail chart, and the accounting P&L all read
      // this table under their own keys.
      for (const key of [
        ["rent-payments", workspaceId],
        ["rent_payments_owned", workspaceId],
        ["property-rent-payments"],
        ["accounting-rents", workspaceId],
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      toast.success("Payment deleted");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to delete payment")),
  });

  // ---- Tenant Messages ----
  const { data: messages = [], isLoading: loadingMessages } = useQuery({
    queryKey: ["tenant-messages", workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_messages")
        .select("*")
        .eq("workspace_id", workspaceId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as unknown as TenantMessage[];
    },
    enabled: !!workspaceId,
  });

  // Realtime for messages
  useEffect(() => {
    if (!workspaceId) return;
    const channel = supabase
      .channel("tenant-messages-rt")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "tenant_messages",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["tenant-messages", workspaceId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [workspaceId, queryClient]);

  const sendMessage = useMutation({
    mutationFn: async (msg: {
      pipeline_property_id: string;
      tenant_name: string;
      tenant_contact?: string;
      message: string;
      sender_type?: string;
    }) => {
      const { error } = await supabase.from("tenant_messages").insert({
        ...msg,
        workspace_id: workspaceId,
        sender_type: msg.sender_type || "landlord",
        created_by_user_id: user!.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-messages", workspaceId],
      });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to send message")),
  });

  // ---- PM Settings ----
  const { data: pmSettings = [], isLoading: loadingSettings } = useQuery({
    queryKey: ["pm-settings", workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("property_management_settings")
        .select("*")
        .eq("workspace_id", workspaceId!);
      if (error) throw error;
      return data as unknown as PMSettings[];
    },
    enabled: !!workspaceId,
  });

  const upsertSettings = useMutation({
    mutationFn: async (s: Partial<PMSettings>) => {
      const { error } = await supabase
        .from("property_management_settings")
        .upsert(
          {
            ...s,
            workspace_id: workspaceId,
          } as never,
          { onConflict: "workspace_id,pipeline_property_id" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pm-settings", workspaceId] });
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to save settings")),
  });

  return {
    workOrders,
    loadingWorkOrders,
    createWorkOrder,
    updateWorkOrder,
    deleteWorkOrder,
    rentPayments,
    loadingPayments,
    createPayment,
    updatePayment,
    deletePayment,
    messages,
    loadingMessages,
    sendMessage,
    pmSettings,
    loadingSettings,
    upsertSettings,
  };
}
